import { HttpContext } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { EMPTY, from, timer, catchError, concatMap, finalize, bufferCount, takeWhile } from 'rxjs';
import { SKIP_LOADER } from 'src/app/interceptors/auth/http-context-tokens';
import { DriverAppLog } from 'src/app/models/DriverAppLog.model';
import { ApiService } from 'src/app/services/api/api.service';
import { SqliteStorageService } from 'src/app/services/sqlite-storage/sqlite-storage.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ErrorLoggingService {

  private isSyncing = false;
  private stopTimer = false;
  STORAGE_KEY = "ErrorLogs";

  errorLogs = signal<DriverAppLog[]>([]);

  constructor(
    private readonly apiService: ApiService,
    private readonly sqlliteService: SqliteStorageService
  ) {
  }

  /// Init error logging when application starts
  async initializeErrorLogging() {
    await this.loadFromStorage();
    if (navigator.onLine && environment.errorSyncTime == 0) {
      await this.syncErrorsImmediately();
    }
    else if (navigator.onLine && environment.errorSyncTime > 0) {
      this.syncErrosPeriodically();
    }
  }

  /// function to save  errors into local db
  async addErrorLog(log: DriverAppLog) {
    this.errorLogs.update(value => [...value, log]);
    await this.saveToStorage();

    //condition to check whether send error immediately or periodically
    if (navigator.onLine && environment.errorSyncTime == 0) {
      await this.syncErrorsImmediately();
    }
    else if (navigator.onLine && this.stopTimer && environment.errorSyncTime > 0) {
      this.stopTimer = false;
      this.syncErrosPeriodically();
    }
  }

  ///function to sync errors periodically, i.e when 'errorSyncTime > 0
  ///stop timer if API throws error
  private syncErrosPeriodically() {
    //console.log('--syncErrosPeriodically--');
    timer(0, environment.errorSyncTime * 1000)
      .pipe(takeWhile(() => !this.stopTimer))
      .subscribe(async () => {

        if (!navigator.onLine) return;
        if (this.errorLogs().length === 0) return;
        if (this.isSyncing) return;

        const batch = this.errorLogs().slice(0, environment.errorBatchSize);
        await this.submitErrorLogs(batch);
      });

  }

  ///function to sync errors immediately, i.e when 'errorSyncTime = 0
  ///process batch wise (it will manage offline mode as well)
  private async syncErrorsImmediately() {
    //console.log('--syncErrorsImmediately--');
    // batch processing for:
    // 1. handle offline mode(send errors which are stored when network resumes)
    // 2. logs are more than 'maxBatchSize'
    // 3. if online - send batch of available errors.
    from(this.errorLogs()).pipe(
      bufferCount(environment.errorBatchSize),

      concatMap(batch => this.submitErrorLogs(batch))

    ).subscribe({
      next: (res) => console.log('Batch sent', res),
      error: (err) => console.error('Batch error', err),
      complete: () => console.log('All batches processed')
    });

  }

  /// function to submit errors
  private async submitErrorLogs(logs: DriverAppLog[]) {
    //console.log('--submitErrorLogs--');
    this.isSyncing = true;
    this.apiService.post('errorLogs/addErrorLogs', logs, { context: new HttpContext().set(SKIP_LOADER, true) })
      .pipe(
        concatMap(() => from(this.persistSyncedBatch(logs.length))),
        catchError((err) => {
          this.stopTimer = true;
          console.log('Error sync failed', err);
          console.log(this.errorLogs());
          return EMPTY;
        }),
        finalize(() => {
          this.isSyncing = false;
        })
      )
      .subscribe();
  }

  //#region private functions

  ///function to update error logs after successful syncing
  private async persistSyncedBatch(batchSize: number) {
    const currentLogs = this.errorLogs();
    const remainingLogs = currentLogs.slice(batchSize);

    this.errorLogs.set(remainingLogs);

    try {
      await this.saveToStorage();
    } catch (err) {
      this.errorLogs.set(currentLogs);
      throw new Error('Error updating error-logs into SQLiteStorage, ' + err);
    }
  }

  ///function to save error into sqllite
  private async saveToStorage() {
    await this.sqlliteService.setItem(this.STORAGE_KEY, this.errorLogs());
  }

  ///function to fetch error logs from sqllite storage and assing to errorLogs var
  ///used while initialising App
  private async loadFromStorage() {
    try {
      const stored = await this.sqlliteService.getItem<DriverAppLog[]>(this.STORAGE_KEY);
      if (stored && Array.isArray(stored)) {
        this.errorLogs.set(stored);
      }
    } catch (err) {
      console.error('Failed loading stored errors', err);
    }
  }
  //#endregion
}
